import { useState } from 'react';

// material-ui
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Box from '@mui/material/Box';

// project imports
import { Notification } from './data';
import NotificationContent from './NotificationContent';
import IconButton from 'components/@extended/IconButton';
import usePortalNotifications from 'hooks/usePortalNotifications';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const extractRequestLink = (text: string): string | undefined => {
  if (!text) return undefined;

  // Support both relative and absolute URLs containing /requests/{id}
  const absoluteMatch = text.match(/https?:\/\/[^\s)]+\/requests\/[A-Za-z0-9-]+/i);
  if (absoluteMatch?.[0]) {
    try {
      const parsed = new URL(absoluteMatch[0]);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return undefined;
    }
  }

  const relativeMatch = text.match(/\/requests\/[A-Za-z0-9-]+/i);
  return relativeMatch?.[0];
};

export default function NotificationMenu() {
  const { notifications, unreadCount, loading, error, refresh, markAsRead } = usePortalNotifications();
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen(!open);

  const [filter, setFilter] = useState<'all' | 'unread' | 'mentions'>('all');
  const handleFilterChange = (value: 'all' | 'unread' | 'mentions') => () => {
    setFilter(value);
  };

  const mappedNotifications: Notification[] = notifications.map((n) => {
    const combinedContent = `${n.title || ''}\n${n.summary || ''}\n${n.body || ''}`;

    return {
    id: n.id,
    notificationId: n.notificationId,
    category: n.category,
    type: n.type,
    requestLink: extractRequestLink(combinedContent),
    user: n.sender
      ? {
          name: n.sender.name,
          avatar: ''
        }
      : null,
    title: n.title,
    message: n.summary || n.body || 'New notification',
    time: n.createdAt ? new Date(n.createdAt).toLocaleString() : 'Just now',
    createdAt: n.createdAt,
    read: Boolean(n.read),
    context: undefined,
  };
  });

  // Logic to filter notifications
  const filteredNotifications = mappedNotifications.filter((notification) => {
    if (!notification) return false;
    switch (filter) {
      case 'unread':
        return !notification.read;
      case 'mentions':
        return notification.category === 'mention' || notification.type === 'mention';
      case 'all':
      default:
        return true;
    }
  });

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification || notification.read) return;
    await markAsRead(notification.id);
  };

  return (
    <>
      {/* Notification Icon Button */}
      <Box sx={{ flexShrink: 0 }}>
        <Tooltip title="Notification">
          <IconButton
            color="secondary"
            variant="light"
            sx={(theme) => ({
              color: 'text.primary',
              bgcolor: open ? 'grey.100' : 'transparent',
              ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
            })}
            aria-label="open notifications"
            onClick={handleToggle}
          >
            <Badge badgeContent={unreadCount} color="primary">
              <BellOutlined />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {/* Notification Drawer */}
      <Drawer
        sx={{ zIndex: 2001 }}
        anchor="right"
        onClose={handleToggle}
        open={open}
        slotProps={{ paper: { sx: { width: { xs: 340, sm: 440 } } } }}
      >
        {open && (
          <NotificationContent
            notifications={filteredNotifications}
            totalCount={mappedNotifications.length}
            unreadCount={unreadCount}
            loading={loading}
            error={error}
            filter={filter}
            setFilter={handleFilterChange}
            onNotificationClick={handleNotificationClick}
            onRefresh={refresh}
          />
        )}
      </Drawer>
    </>
  );
}
